"use client"

import * as React from "react"

import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  arenaMoney,
  ARENA_BRANCH,
  ARENA_EYEBROW,
  ARENA_SURFACE,
  ArenaSectionHeading,
  RefundNotice,
  ScoreValue,
} from "@/components/arena/v2/primitives"
import {
  EXPERIENCE_TIER_LABEL,
  PICK_COUNT_RULES,
  PICK_SCORE,
  buildGateBands,
  formatMultiple,
  formatUsd,
  type ExperienceTier,
  type PickCount,
} from "@/lib/arena/v2-types"
import { cn } from "@/lib/utils"

/* =============================================================================
   Arena V2, the explainer.
   -----------------------------------------------------------------------------
   Arena binds automatically the moment a lineup is submitted, so this
   is the only surface that explains the mechanic BEFORE it costs the player
   anything. It is opened from the slip and from the group board, which means it
   has to read as a page section and as the body of a centered dialog without
   changing shape.

   How it teaches:
   - One worked example carries the whole document. A $5.00 five-pick lineup is
     priced, scored, dropped one pick, and then run through the group-size table.
     Every figure below comes from PICK_COUNT_RULES / PICK_SCORE, so the
     explainer cannot drift from the rules the rest of Arena renders.
   - The prize ledger appears here in the same two-row form it takes on every
     other Arena surface, so the object a player learns here is the object they
     meet again on their entry.
   - No icon grid, no feature cards, no step circles. Six ruled sections, each
     ending in the number the player actually needs.

   Accent: this document carries no eyebrow of its own, in the page block or in
   the dialog header. A mono "Arena" label sitting on a title that already reads
   "How Arena works" names the system twice in twelve pixels. The single accent
   left is the 2px rule on the perfect-lineup ledger row, which is the ledger's
   own motif on every Arena surface.

   Figures: when a caller passes the reader's own pickCount and entryFeeUsd the
   ledger is labelled as theirs and only the multiple is caveated, because the
   multiple really is generic. With no props it runs on the worked example and
   says so once, in the ledger caption.
   ============================================================================= */

/* -------------------------------------------------------------------------- */
/* The worked example                                                          */
/* -------------------------------------------------------------------------- */

interface WorkedExample {
  pickCount: PickCount
  entryFeeUsd: number
  /** How many picks of each kind the example lineup carries. */
  litPicks: number
  standardPicks: number
  icePicks: number
  /** Score if every pick hits. */
  perfectScore: number
  /** Score with the ICE pick missed, which is the second half of the lesson. */
  oneMissScore: number
  /** Quoted guarantee used by the example. */
  guaranteeMultiple: number
  perfectPrizeUsd: number
  topScoreMultiple: number
  topScorePrizeUsd: number
  refundAtOrBelow: number
  topScoreLiveAt: number
}

const round2 = (value: number) => Math.round(value * 100) / 100

function buildExample(pickCount: PickCount, entryFeeUsd: number): WorkedExample {
  const rule = PICK_COUNT_RULES[pickCount]

  // One ICE pick and, from four picks up, two LIT picks. The mix matters: it is
  // what makes the ICE lesson land rather than a lineup of identical picks.
  const icePicks = 1
  const litPicks = pickCount >= 4 ? 2 : 1
  const standardPicks = pickCount - icePicks - litPicks

  const perfectScore = round2(
    litPicks * PICK_SCORE.lit + standardPicks * PICK_SCORE.normal + icePicks * PICK_SCORE.ice
  )

  return {
    pickCount,
    entryFeeUsd,
    litPicks,
    standardPicks,
    icePicks,
    perfectScore,
    oneMissScore: round2(perfectScore - PICK_SCORE.ice),
    guaranteeMultiple: rule.maxGuarantee,
    perfectPrizeUsd: round2(entryFeeUsd * rule.maxGuarantee),
    topScoreMultiple: rule.leaderboardMultiplier,
    topScorePrizeUsd: round2(entryFeeUsd * rule.leaderboardMultiplier),
    refundAtOrBelow: rule.refundAtOrBelow,
    topScoreLiveAt: rule.topScoreLiveAt,
  }
}

/* -------------------------------------------------------------------------- */
/* Section frame                                                               */
/* -------------------------------------------------------------------------- */

interface SectionProps {
  /** "01", a plain ruled ordinal rather than a step circle. */
  ordinal: string
  title: string
  children: React.ReactNode
}

function Section({ ordinal, title, children }: SectionProps) {
  return (
    <section className="border-b border-grey-800 pb-5 last:border-b-0 last:pb-0">
      <h3 className="flex items-baseline gap-2 font-open-sauce text-[15px] font-semibold tracking-tight text-white">
        <span className="font-mono text-[11px] font-semibold tabular-nums text-grey-300">
          {ordinal}
        </span>
        {title}
      </h3>
      <div className="mt-2.5">{children}</div>
    </section>
  )
}

function Body({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <p className={cn("text-[13px] leading-relaxed text-grey-300", className)}>{children}</p>
  )
}

/* -------------------------------------------------------------------------- */
/* The three grouping dimensions                                               */
/* -------------------------------------------------------------------------- */

/** Fewest settled lineups first. The order the four bands are always listed in. */
const TIER_ORDER: ExperienceTier[] = ["rookie", "regular", "seasoned", "veteran"]

/**
 * The three things that have to match exactly before two lineups can meet.
 * Pick count and settlement day are visible on the slip; experience tier is not,
 * and is the one dimension a player has no other way to learn about.
 */
function GroupingDimensions({ pickCount }: { pickCount: PickCount }) {
  const dimensions: { term: string; detail: string }[] = [
    {
      term: "Pick count",
      detail: `A ${pickCount}-pick lineup only meets other ${pickCount}-pick lineups.`,
    },
    {
      term: "Settlement day",
      detail:
        "The day the last game in your lineup finishes. The group settles together, so nothing is paid until the slowest game in it is done, which may not be one of yours.",
    },
    {
      term: "Experience tier",
      detail: "One of four bands, set by how many real-money lineups you have settled.",
    },
  ]

  return (
    <div className={ARENA_SURFACE.card}>
      <div>
        {dimensions.map((dimension) => (
          <div
            key={dimension.term}
            className="grid grid-cols-[104px_1fr] gap-x-3 border-b border-grey-800 py-2.5 first:pt-0 last:border-b-0 last:pb-0"
          >
            <span className="text-[13px] font-semibold tracking-tight text-white">
              {dimension.term}
            </span>
            <span className="text-[12px] leading-relaxed text-grey-300">
              {dimension.detail}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-3 border-t border-grey-800 pt-3">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {TIER_ORDER.map((tier, index) => (
            <React.Fragment key={tier}>
              {index > 0 && (
                <span aria-hidden className="text-[12px] text-grey-600">
                  /
                </span>
              )}
              <span className="text-[13px] font-semibold tracking-tight text-grey-100">
                {EXPERIENCE_TIER_LABEL[tier]}
              </span>
            </React.Fragment>
          ))}
        </div>
        <p className="mt-1.5 text-[12px] leading-relaxed text-grey-300">
          Fewest settled lineups first. You do not choose your tier and cannot see anyone
          else&apos;s.
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-grey-200">
          If a tier is too thin to fill a group on its own, its lineups are folded into
          the tier below, capped at 10% of that group.
        </p>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The prize ledger, in the same two-row form it takes everywhere else         */
/* -------------------------------------------------------------------------- */

interface LedgerRowProps {
  branch: "perfect" | "top-score"
  label: string
  condition: string
  multiple: number
  amountUsd: number
}

function LedgerRow({ branch, label, condition, multiple, amountUsd }: LedgerRowProps) {
  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-4 py-3">
      <div className="flex gap-3">
        <span
          aria-hidden
          className={cn(
            "w-[2px] shrink-0 rounded-full",
            ARENA_BRANCH[branch === "perfect" ? "perfect" : "top"].rule
          )}
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold tracking-tight text-white">
            {label}
          </span>
          <span className="mt-0.5 block text-[12px] leading-relaxed text-grey-300">
            {condition}
          </span>
        </span>
      </div>
      <div className="flex flex-col items-end gap-0.5">
        <span className="text-[13px] font-semibold tabular-nums text-grey-200">
          {formatMultiple(multiple)}
        </span>
        <span className={arenaMoney("major", "live")}>{formatUsd(amountUsd)}</span>
      </div>
    </div>
  )
}

/**
 * The hairline that carries the no-stacking rule. It gets its own treatment so
 * it cannot be mistaken for body copy: two rules and a centred label, sitting
 * between the two branches it adjudicates.
 */
function HigherOfTheTwo() {
  return (
    <div className="flex items-center gap-3 py-1">
      <span aria-hidden className="h-px flex-1 bg-grey-800" />
      <span className={ARENA_EYEBROW}>
        Higher of the two
      </span>
      <span aria-hidden className="h-px flex-1 bg-grey-800" />
    </div>
  )
}

function PrizeLedgerFragment({
  example,
  isOwnLineup,
}: {
  example: WorkedExample
  isOwnLineup: boolean
}) {
  return (
    <div className={ARENA_SURFACE.card}>
      <div className="flex items-baseline justify-between gap-3">
        <span className={ARENA_EYEBROW}>
          Prize ledger
        </span>
        <span className="text-[12px] tabular-nums text-grey-300">
          {isOwnLineup ? "Your" : "Example:"} {formatUsd(example.entryFeeUsd)} entry,{" "}
          {example.pickCount} picks
        </span>
      </div>

      <div className="mt-1">
        <LedgerRow
          branch="perfect"
          label="Perfect lineup"
          condition="Every pick hits"
          multiple={example.guaranteeMultiple}
          amountUsd={example.perfectPrizeUsd}
        />
        <HigherOfTheTwo />
        <LedgerRow
          branch="top-score"
          label="Top score"
          condition="Highest score in the group"
          multiple={example.topScoreMultiple}
          amountUsd={example.topScorePrizeUsd}
        />
      </div>

      {/* The caption above already says "Example" when the figures are not the
       * reader's. When they are, the fee and the pick count are theirs and only
       * the multiple is generic, so that is the only thing left to caveat. */}
      {isOwnLineup && (
        <p className="mt-3 border-t border-grey-800 pt-3 text-[12px] leading-relaxed text-grey-300">
          Your guarantee is the multiple quoted on your lineup. LIT and ICE picks move it.
        </p>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Scoring, shown as the worked example resolving                              */
/* -------------------------------------------------------------------------- */

interface ScoreLineProps {
  count: number
  label: string
  weight: number
  subtotal: number
}

function ScoreLine({ count, label, weight, subtotal }: ScoreLineProps) {
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-baseline gap-x-3 border-b border-grey-800 py-2 last:border-b-0">
      <span className="text-[13px] text-grey-200">
        <span className="tabular-nums text-white">{count}</span> {label}
      </span>
      <span className="text-[12px] tabular-nums text-grey-300">
        {weight.toFixed(2)} each
      </span>
      <span className="w-[52px] text-right text-[13px] font-semibold tabular-nums text-white">
        {subtotal.toFixed(2)}
      </span>
    </div>
  )
}

function ScoringFragment({
  example,
  isOwnLineup,
}: {
  example: WorkedExample
  isOwnLineup: boolean
}) {
  const lines: ScoreLineProps[] = [
    {
      count: example.litPicks,
      label: example.litPicks === 1 ? "LIT pick" : "LIT picks",
      weight: PICK_SCORE.lit,
      subtotal: round2(example.litPicks * PICK_SCORE.lit),
    },
    {
      count: example.standardPicks,
      label: example.standardPicks === 1 ? "standard pick" : "standard picks",
      weight: PICK_SCORE.normal,
      subtotal: round2(example.standardPicks * PICK_SCORE.normal),
    },
    {
      count: example.icePicks,
      label: example.icePicks === 1 ? "ICE pick" : "ICE picks",
      weight: PICK_SCORE.ice,
      subtotal: round2(example.icePicks * PICK_SCORE.ice),
    },
  ].filter((line) => line.count > 0)

  return (
    <div className={ARENA_SURFACE.card}>
      {/* The pick mix below is invented either way. It only needs saying when
       * the ledger above has just been labelled as the reader's own lineup,
       * because then the document is otherwise claiming to know their picks. */}
      {isOwnLineup && (
        <span className="mb-2 block text-[12px] leading-relaxed text-grey-300">
          Example pick mix. Every lineup scores on the same weights.
        </span>
      )}
      {/* Stacked under 640px: side-by-side leaves ~69px for line labels inside the sheet. */}
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          {lines.map((line) => (
            <ScoreLine key={line.label} {...line} />
          ))}
        </div>
        <ScoreValue
          value={example.perfectScore}
          size="hero"
          showLabel
          label="If every pick hits"
          className="shrink-0 items-start text-left sm:items-end sm:text-right"
        />
      </div>

      <p className="mt-3 border-t border-grey-800 pt-3 text-[13px] leading-relaxed text-grey-200">
        Miss the ICE pick and this lineup scores {example.oneMissScore.toFixed(2)}. The
        guarantee is gone, but {example.oneMissScore.toFixed(2)} can still be the highest
        score in the group and win {formatUsd(example.topScorePrizeUsd)}.
      </p>
      <p className="mt-2 text-[13px] leading-relaxed text-grey-300">
        A miss, a push, a void and a player who does not appear all score 0.00. ICE picks
        raise the guarantee on your slip and lower your Arena score. LIT picks do the
        opposite.
      </p>
      {/* The score consequence alone reads as though the payout is untouched.
       * A push, a void or an absent player also takes the pick out of the
       * lineup, which repays it on a shorter table: the guarantee the reader is
       * holding can move before a single game is decided. */}
      <p className="mt-2 text-[13px] leading-relaxed text-grey-300">
        A push, a void and an absent player also drop out of the lineup. What is left
        settles as a lineup one pick shorter and is paid on that table, so the guarantee
        quoted when you submitted comes down with it.
      </p>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* The gate table                                                              */
/* -------------------------------------------------------------------------- */

const GATE_ROWS: PickCount[] = [2, 3, 4, 5, 6]

const GATE_GRID = "grid grid-cols-[58px_1fr_1fr_1fr_64px] gap-x-3"

/**
 * Every pick count's ladder side by side. Headings and cells both come from
 * buildGateBands, the same function the pre-lock ladder and the post-lock
 * meter render, so the table can never name a band the rest of Arena does not.
 */
function GateTable({ pickCount }: { pickCount: PickCount }) {
  // The band titles do not vary by pick count, only the ranges do, so any row
  // can supply the headings.
  const headings = buildGateBands(
    PICK_COUNT_RULES[pickCount].refundAtOrBelow,
    PICK_COUNT_RULES[pickCount].topScoreLiveAt
  )

  return (
    <div className="rounded-2xl border border-grey-800 bg-grey-950 p-1">
      <div className="overflow-x-auto thin-scrollbar">
        <div className="min-w-[420px]">
          <div
            className={cn(
              GATE_GRID,
              "border-b border-grey-800 px-3 py-2.5 text-[11px] text-grey-300"
            )}
          >
            <span>Picks</span>
            {headings.map((band) => (
              <span key={band.key}>{band.title}</span>
            ))}
            <span className="text-right">Top score</span>
          </div>

          {GATE_ROWS.map((count) => {
            const rule = PICK_COUNT_RULES[count]
            const bands = buildGateBands(rule.refundAtOrBelow, rule.topScoreLiveAt)
            const isExample = count === pickCount
            return (
              <div
                key={count}
                className={cn(
                  GATE_GRID,
                  "items-baseline border-b border-grey-800 px-3 py-2.5 text-[13px] tabular-nums last:border-b-0",
                  isExample ? "bg-grey-900 text-white" : "text-grey-200"
                )}
              >
                <span className={cn(isExample && "font-semibold")}>{count}</span>
                {bands.map((band) => (
                  <span key={band.key} className={cn(!isExample && "text-grey-300")}>
                    {band.compactRange}
                  </span>
                ))}
                <span className="text-right">{formatMultiple(rule.leaderboardMultiplier)}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Main component                                                              */
/* -------------------------------------------------------------------------- */

/** What the document runs on when the caller does not know the reader's lineup. */
const EXAMPLE_PICK_COUNT: PickCount = 5
const EXAMPLE_ENTRY_FEE_USD = 5

export interface HowArenaV2WorksProps extends React.ComponentProps<"div"> {
  /**
   * The reader's own pick count. Pass it together with entryFeeUsd and the
   * ledger stops calling their lineup an example. Omit both for the worked one.
   */
  pickCount?: PickCount
  /** The reader's own entry fee. See pickCount. */
  entryFeeUsd?: number
  /**
   * The dialog renders its own title in the header, so the section heading is
   * suppressed there to avoid saying the same thing twice.
   */
  showHeading?: boolean
}

/**
 * The Arena explainer. Renders as a plain block, so it drops into a page
 * section, a bottom sheet or the dialog below without changing shape.
 */
export function HowArenaV2Works({
  pickCount,
  entryFeeUsd,
  showHeading = true,
  className,
  ...props
}: HowArenaV2WorksProps) {
  const isOwnLineup = pickCount !== undefined && entryFeeUsd !== undefined
  const example = React.useMemo(
    () =>
      buildExample(
        pickCount ?? EXAMPLE_PICK_COUNT,
        entryFeeUsd ?? EXAMPLE_ENTRY_FEE_USD
      ),
    [pickCount, entryFeeUsd]
  )

  const lowestGuarantee = PICK_COUNT_RULES[2].maxGuarantee
  const highestGuarantee = PICK_COUNT_RULES[6].maxGuarantee

  return (
    <div className={cn("space-y-5", className)} {...props}>
      {showHeading && (
        <ArenaSectionHeading
          title="How Arena works"
          description="Every lineup of 2 to 6 picks plays in Arena. No opt-in, no extra entry fee."
        />
      )}

      <Section ordinal="01" title="Your lineup">
        <Body>
          Your entry fee and the guarantee quoted on your slip do not change. The
          guarantee runs from around {formatMultiple(lowestGuarantee)} on two picks to
          around {formatMultiple(highestGuarantee)} on six, higher with LIT picks and
          lower with ICE.
        </Body>
      </Section>

      <Section ordinal="02" title="The group">
        <Body>
          Three things have to match before two lineups can meet. Entry fee is not one of
          them: each lineup plays for a multiple of its own fee.
        </Body>
        <div className="mt-3">
          <GroupingDimensions pickCount={example.pickCount} />
        </div>
        <Body className="mt-3">
          One lineup per player, up to 64 lineups per group. Lineups stay hidden until the
          group locks, five minutes before its first game. You can cancel for a full
          refund for five minutes after you submit.
        </Body>
      </Section>

      <Section ordinal="03" title="The two prizes">
        <PrizeLedgerFragment example={example} isOwnLineup={isOwnLineup} />
        {/* The in-product ledgers carry the no-stacking rule on the hairline
         * alone. This is the surface that teaches the mechanic cold, so it is
         * the one place the rule is spelled out rather than implied. */}
        <Body className="mt-3">
          Qualify for both and only the higher one pays. They never stack.
        </Body>
      </Section>

      <Section ordinal="04" title="Scoring">
        <ScoringFragment example={example} isOwnLineup={isOwnLineup} />
      </Section>

      <Section ordinal="05" title="Group size">
        <Body>Lineups in the group, by pick count.</Body>
        <div className="mt-3">
          <GateTable pickCount={example.pickCount} />
        </div>
        {/* The table above already prints the three bands against the reader's
         * own pick count. What it cannot say is how the top score prize is
         * divided once it runs, and the near-duplicate rule is the one rule in
         * the mechanic that can cut a prize already quoted. */}
        <p className="mt-3 text-[13px] leading-relaxed text-grey-200">
          The top score prize is split between every lineup tied on it. Lineups sharing
          more than 3 picks with each other count as one and take a single share.
        </p>
      </Section>

      <Section ordinal="06" title="Refunds">
        {/* The explainer is the one surface teaching the mechanic cold, so it
         * lists all three full-refund guarantees. The waiting screen shows the
         * group-size floor alone. */}
        <RefundNotice
          className="mt-1"
          guarantees={["not-matched", "waited-too-long", "group-too-small"]}
        />
        <div className="mt-3 rounded-xl border border-grey-800 bg-grey-900 px-3 py-2.5">
          <p className="border-b border-grey-800 pb-2.5 text-[13px] leading-relaxed text-grey-200">
            No lineup in the group scores at all. Ten percent of every entry fee is
            returned.
          </p>
          <p className="pt-2.5 text-[13px] leading-relaxed text-grey-200">
            Voided picks leave you with fewer than two. Entry fee returned in full and the
            group settles without your lineup.
          </p>
        </div>
      </Section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Dialog wrapper                                                              */
/* -------------------------------------------------------------------------- */

export interface HowArenaV2WorksDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  pickCount?: PickCount
  entryFeeUsd?: number
}

/**
 * The same explainer as a centered dialog, which is how the slip and the group
 * board open it. The header stays put while the document scrolls, so a reader
 * who has scrolled to the gate table still knows what they are reading.
 */
export function HowArenaV2WorksDialog({
  open,
  onOpenChange,
  pickCount,
  entryFeeUsd,
}: HowArenaV2WorksDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[86dvh] w-full max-w-[560px] gap-0 overflow-hidden border-grey-800 bg-grey-950 p-0">
        <div className="border-b border-grey-800 px-5 py-4 pr-12">
          <DialogTitle className="font-open-sauce text-[15px] font-semibold tracking-tight text-white">
            How Arena works
          </DialogTitle>
          <p className="mt-1.5 text-[13px] leading-relaxed text-grey-300">
            Every lineup of 2 to 6 picks plays in Arena. No opt-in, no extra entry
            fee.
          </p>
        </div>

        <div className="max-h-[calc(86dvh-116px)] overflow-y-auto thin-scrollbar px-5 pt-5 pb-[calc(20px+env(safe-area-inset-bottom))] sm:pb-5">
          <HowArenaV2Works
            pickCount={pickCount}
            entryFeeUsd={entryFeeUsd}
            showHeading={false}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
